import axios, { type AxiosError } from 'axios'

/** Shape of error bodies returned by the Craft IDE PHP endpoints. */
interface ApiErrorBody {
  error?:   string
  message?: string
}

const apiClient = axios.create({
  baseURL: import.meta.env.VITE_API_BASE || '/',
  withCredentials: true,
  timeout: 15_000,
  headers: { 'Content-Type': 'application/json' },
})

// Runtime CSRF token, populated by initSession() in App.vue.
// Falls back to the meta tag (empty string until session init completes).
let runtimeCsrfToken = ''

/**
 * Store the CSRF token received from GET /api/ide/session.
 * Called once by App.vue before any mutating API requests are made.
 */
export function setCsrfToken(token: string): void {
  runtimeCsrfToken = token
}

// Security: attach Craft CSRF token to every mutating request.
// Prefers the runtime token set by setCsrfToken(); falls back to the meta tag
// so the interceptor still works if called before session init (e.g. in tests).
apiClient.interceptors.request.use((config) => {
  const metaToken =
    document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.getAttribute('content') ?? ''
  const csrfToken = runtimeCsrfToken || metaToken

  if (['post', 'put', 'patch', 'delete'].includes(config.method?.toLowerCase() ?? '')) {
    config.headers['X-CSRF-Token'] = csrfToken
  }

  return config
})

apiClient.interceptors.response.use(
  (response) => response,
  (error: AxiosError<ApiErrorBody>) => {
    const status  = error.response?.status
    const message = extractMessage(error)

    if (status === 403) console.error('[apiClient] Forbidden:', message)
    if (status === 429) console.error('[apiClient] Rate limit hit:', message)

    return Promise.reject(error)
  }
)

/**
 * Extract a human-readable message from any thrown value.
 *
 * Checks the Craft IDE API error body shape first (`error` key, then
 * `message` key), then falls back to the Axios message or String().
 */
export function extractMessage(err: unknown): string {
  if (axios.isAxiosError(err)) {
    const body = (err as AxiosError<ApiErrorBody>).response?.data
    return body?.error ?? body?.message ?? err.message
  }
  return err instanceof Error ? err.message : String(err)
}

export default apiClient
